import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MAX_REF_IMGS } from './constants'
import {
  apiFetchMeinianAllCategory,
  apiFetchMeinianCollectUsers,
  apiFetchMeinianCommonMenuItems,
  apiFetchMeinianGallery,
  apiFetchMeinianInspirationTree,
  apiFetchMeinianPlatforms,
  apiUploadTmpImg,
} from './service'

const PAGE_SIZE = 30
const TAOXI_PLATFORM_VALUE = '8'

const COMMON_PLATFORM_OPTIONS = [
  { label: 'INS款式', value: '11' },
  { label: '品牌预售', value: '9' },
  { label: '批发市场', value: '7' },
  { label: '零售市场', value: '6' },
  { label: '社媒', value: '37' },
  { label: '得物', value: '38' },
  { label: '街拍', value: '39' },
  { label: '订货会', value: '40' },
  { label: 'INS商品', value: '41' },
  { label: 'Pinterest', value: '50' },
  { label: '微博', value: '20' },
  { label: '秀场', value: '2' },
  { label: '淘系', value: '8' },
  { label: '上传灵感', value: '26' },
  { label: '抖衣', value: '18' },
  { label: '抖衣作品', value: '19' },
  { label: '得物商品', value: '45' },
  { label: '秀场细节', value: '46' },
  { label: '时装大片', value: '47' },
  { label: '海外探款', value: '100' },
  { label: '海外探款-INS商品', value: '98' },
  { label: '海外探款-INS款式', value: '97' },
  { label: '海外探款-秀场', value: '99' },
  { label: '海外探款-Pinterest', value: '96' },
  { label: '海外探款-Tiktok', value: '95' },
  { label: '海外探款-图案', value: '201' },
  { label: '淘系搭配', value: '571' },
  { label: '采集插件', value: '27' },
  { label: 'FD+智能设计', value: '28' },
  { label: 'FD素材库', value: '29' },
  { label: '知款-图案', value: '31' },
]

const emptyFilters = () => ({ selectedStyles: [] })
const getNodeName = item => item?.name || item?.itemName || ''
const getChildren = item => item?.children || item?.itemTreeValue?.children || []
const isValidImageId = id => id !== undefined && id !== null && String(id).trim() !== ''
const getImageId = item => [item?.id, item?.imageId, item?.unionId].find(isValidImageId) ?? null
const getImageKey = item => getImageId(item) ?? item?.mainUrl ?? `${item?.imageGroupEntityId || ''}-${item?.inspirationId || ''}-${item?.mainUrl || ''}`
const getGalleryImageUrl = item => item?.convertedMainUrl || item?.mainUrl
const getGalleryImageUrls = item => Array.from(new Set([item?.convertedMainUrl, item?.mainUrl, item?.originMainUrl].filter(Boolean)))
const toList = res => (Array.isArray(res) ? res : (res?.resultList || res?.list || res?.records || res?.data || res?.result || []))
const toObject = res => (Array.isArray(res) ? { children: res } : (res?.data || res?.result || res || null))
const getUploadedImageUrl = res => {
  if (typeof res === 'string') return res
  if (typeof res?.data === 'string') return res.data
  if (typeof res?.result === 'string') return res.result
  return res?.url || res?.mainUrl || res?.data?.url || res?.data?.mainUrl || res?.result?.url || res?.result?.mainUrl || ''
}
const findSelectedRefImage = (item, refImgs = []) => {
  const id = getImageId(item)
  const urls = getGalleryImageUrls(item)
  return refImgs.find(refImg => urls.includes(refImg.url) || (id != null && refImg.id != null && String(refImg.id) === String(id)))
}
const isSelectedImage = (item, refImgs = []) => Boolean(findSelectedRefImage(item, refImgs))
const dedupeGalleryList = list => {
  const seen = new Set()
  return list.filter(item => {
    const key = getImageKey(item) || item.mainUrl
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function getZKPlatformValue(result = []) {
  const ids = []
  result.forEach(platform => {
    if (platform.platformType === '服装站点') {
      const platformList = platform.platformList || []
      platformList.forEach(item => {
        if (item.platformId !== undefined) ids.push(item.platformId)
      })
    }
    if (platform.platformType === '独立站') ids.push(-999)
  })
  return ids.join(',')
}

function getPlatformOptions(platformGroups) {
  const zkValue = getZKPlatformValue(platformGroups)
  return zkValue ? [...COMMON_PLATFORM_OPTIONS, { label: '知款站点', value: zkValue }] : COMMON_PLATFORM_OPTIONS
}

function findItemByValue(value, options = []) {
  for (const item of options) {
    if (getNodeName(item) === value) return item
    const childItem = findItemByValue(value, getChildren(item))
    if (childItem) return childItem
  }
  return undefined
}

function getMatrix(value, options) {
  if (!value || !options) return []
  const item = findItemByValue(value, getChildren(options))
  const children = getChildren(item)
  if (children.length) return children.map(getNodeName).filter(Boolean)
  return [value]
}

function normalizeCategoryTree(nodes = []) {
  const loop = (items, rootName) => items.map(item => {
    const nextRootName = rootName || item.name
    return {
      ...item,
      rootName: nextRootName,
      children: item.children ? loop(item.children, nextRootName) : [],
    }
  })
  return loop(nodes)
}

function flattenCategoryTree(tree) {
  const result = []
  const loop = (nodes, parentNames = []) => {
    nodes.forEach(node => {
      const pathNames = [...parentNames, node.name].filter(Boolean)
      result.push({ ...node, pathNames, pathLabel: pathNames.join(' / '), displayLabel: pathNames.join('-') })
      if (node.children?.length) loop(node.children, pathNames)
    })
  }
  loop(tree)
  return result
}

function flattenInspirationTree(nodes = []) {
  const result = []
  nodes.forEach(root => {
    const rootName = root.name || ''
    result.push({ ...root, pathLabel: rootName })
    const childs = root.childs || []
    childs.forEach(child => {
      result.push({ ...child, parentName: rootName, pathLabel: `${rootName} / ${child.name || ''}` })
    })
  })
  return result
}

const normalizeInspirationId = id => String(id)

function getStyleOptions(menuItems) {
  const design = menuItems.find(item => item.itemName === '服装设计要素') || menuItems.find(item => item.itemName === '鞋靴设计要素')
  const styleNode = getChildren(design).find(item => item.itemName === '风格' || item.name === '风格')
  return getChildren(styleNode).map(root => ({
    id: getNodeName(root),
    name: getNodeName(root),
    root: getNodeName(root),
    children: getChildren(root).map(child => ({ name: getNodeName(child) })).filter(item => item.name),
  })).filter(item => item.id)
}

function buildStyleMatrix(selectedStyles = []) {
  const values = []
  selectedStyles.forEach(item => {
    if (item.child) {
      values.push(item.child)
      return
    }
    if (item.children?.length) {
      values.push(...item.children.map(child => child.name).filter(Boolean))
      return
    }
    if (item.root || item.name) values.push(item.root || item.name)
  })
  return Array.from(new Set(values))
}

function buildLabelMatrix(filters, menuItems) {
  const labelMatrix = []
  if (filters.category) {
    const categoryMatrix = getMatrix(filters.category, menuItems.find(item => item.itemName === '品类'))
    if (categoryMatrix.length) labelMatrix.push(categoryMatrix)
  }
  const styleMatrix = buildStyleMatrix(filters.selectedStyles)
  if (styleMatrix.length) labelMatrix.push(styleMatrix)
  return labelMatrix
}

function buildMeinianParams(filters, menuItems) {
  const params = {
    queryType: 'all',
    rankStatus: 1,
    sortOrderStatus: 0,
    labelMatrix: [],
    platformIdList: filters.platformIdList,
    gender: filters.gender,
    category: filters.category,
    userIdList: filters.userIdList,
    inspirationIdList: filters.inspirationIdList,
  }
  const labelMatrix = buildLabelMatrix(filters, menuItems)
  if (labelMatrix.length) params.labelMatrix = labelMatrix
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value === undefined || value === null || (key !== 'labelMatrix' && Array.isArray(value) && value.length === 0)) delete params[key]
  })
  return params
}

function SearchBox({ value, onChange, placeholder = '输入关键词搜索' }) {
  return (
    <div className="fdai-gallery-search">
      <span className="fdai-gallery-search-icon" />
      <input value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} />
    </div>
  )
}

const stopMenuScrollPropagation = event => event.stopPropagation()

function FilterDropdown({ label, labelClassName = '', activeCount, clearable, onClear, width = 240, children }) {
  const [open, setOpen] = useState(false)
  const [menuLeft, setMenuLeft] = useState(0)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const updateMenuPosition = () => {
      const filterEl = ref.current
      const panelEl = filterEl?.closest('.fdai-meinian-gallery-panel')
      if (!filterEl || !panelEl) return
      const filterRect = filterEl.getBoundingClientRect()
      const panelRect = panelEl.getBoundingClientRect()
      const rightOverflow = filterRect.left + width - panelRect.right + 12
      setMenuLeft(rightOverflow > 0 ? -rightOverflow : 0)
    }
    const onDocMouseDown = event => {
      if (!ref.current?.contains(event.target)) setOpen(false)
    }
    updateMenuPosition()
    window.addEventListener('resize', updateMenuPosition)
    document.addEventListener('mousedown', onDocMouseDown)
    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      document.removeEventListener('mousedown', onDocMouseDown)
    }
  }, [open, width])

  return (
    <div className="fdai-gallery-filter" ref={ref}>
      <button className={`fdai-gallery-filter-trigger ${open ? 'open' : ''}`} type="button" onClick={() => setOpen(prev => !prev)}>
        <span className={`fdai-gallery-filter-label ${labelClassName}`} title={label}>{label}</span>
        {activeCount ? <span className="fdai-gallery-filter-count">{activeCount}</span> : null}
        {clearable ? (
          <span
            className="fdai-gallery-filter-clear"
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onClear?.()
              setOpen(false)
            }}
          />
        ) : <span className="fdai-gallery-filter-arrow" />}
      </button>
      {open ? (
        <div
          className="fdai-gallery-filter-menu"
          style={{ width, left: menuLeft }}
          onWheel={stopMenuScrollPropagation}
          onTouchMove={stopMenuScrollPropagation}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      ) : null}
    </div>
  )
}

function PlatformSelect({ value, platformGroups, onChange }) {
  const options = useMemo(() => getPlatformOptions(platformGroups), [platformGroups])
  const platformValue = value?.join(',')
  const selected = options.find(item => item.value === platformValue)
  return (
    <FilterDropdown label={selected?.label || '款式来源'} clearable={!!platformValue} onClear={() => onChange(undefined)} width={220}>
      {({ close }) => (
        <div className="fdai-gallery-option-grid two">
          {options.map(item => (
            <button
              key={item.value}
              type="button"
              className={item.value === platformValue ? 'active' : ''}
              onClick={() => {
                onChange(String(item.value).split(','))
                close()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </FilterDropdown>
  )
}

function CategorySelect({ gender, category, categoryTreeData, onChange }) {
  const [keyword, setKeyword] = useState('')
  const [activeRootName, setActiveRootName] = useState()
  const tree = useMemo(() => normalizeCategoryTree(categoryTreeData?.children || []), [categoryTreeData])
  const flatOptions = useMemo(() => flattenCategoryTree(tree), [tree])
  const selectedOption = useMemo(() => {
    if (!gender) return null
    return flatOptions.find(item => item.rootName === gender && item.name === (category || gender))
  }, [category, flatOptions, gender])
  const currentRoot = tree.find(item => item.name === (activeRootName || gender || tree[0]?.name)) || tree[0]
  const filteredOptions = useMemo(() => {
    const text = keyword.trim().toLowerCase()
    if (!text) return flatOptions
    return flatOptions.filter(item => item.pathLabel.toLowerCase().includes(text))
  }, [flatOptions, keyword])
  const label = selectedOption?.displayLabel || category || gender || '款式品类'

  return (
    <FilterDropdown label={label} clearable={!!(gender || category)} onClear={() => onChange(undefined, undefined)} width={500}>
      {({ close }) => {
        const renderOption = item => {
          const isRoot = item.name === item.rootName
          const active = gender === item.rootName && (isRoot ? !category : category === item.name)
          return (
            <button
              key={`${item.rootName}-${item.pathLabel || item.name}`}
              type="button"
              className={`fdai-gallery-list-option ${active ? 'active' : ''}`}
              title={item.pathLabel || item.name}
              onClick={() => {
                onChange(item.rootName, isRoot ? null : item.name)
                close()
              }}
            >
              {keyword ? item.pathLabel : item.name}
            </button>
          )
        }

        return (
          <div className="fdai-gallery-category-menu">
            <SearchBox value={keyword} onChange={setKeyword} />
            {keyword.trim() ? (
              <div className="fdai-gallery-option-grid three">
                {filteredOptions.length ? filteredOptions.map(renderOption) : <div className="fdai-gallery-filter-empty">暂无相关品类</div>}
              </div>
            ) : (
              <div className="fdai-gallery-cascade">
                <div className="fdai-gallery-cascade-left">
                  {tree.map(root => (
                    <button
                      key={root.name}
                      type="button"
                      className={`${root.name === currentRoot?.name ? 'active' : ''} ${gender === root.name && !category ? 'selected' : ''}`}
                      onMouseEnter={() => setActiveRootName(root.name)}
                      onClick={() => {
                        onChange(root.name, null)
                        close()
                      }}
                    >
                      {root.name}
                    </button>
                  ))}
                </div>
                <div className="fdai-gallery-cascade-right">
                  {currentRoot?.children?.length ? currentRoot.children.map(child => renderOption({ ...child, pathLabel: `${currentRoot.name} / ${child.name}` })) : <div className="fdai-gallery-filter-empty">暂无子品类</div>}
                </div>
              </div>
            )}
          </div>
        )
      }}
    </FilterDropdown>
  )
}

function MultiSearchSelect({ label, value = [], options, onChange }) {
  const [keyword, setKeyword] = useState('')
  const selectedSet = useMemo(() => new Set(value.map(item => String(item))), [value])
  const filteredOptions = useMemo(() => {
    const text = keyword.trim().toLowerCase()
    return options.filter(item => (text ? item.label.toLowerCase().includes(text) : true))
  }, [keyword, options])
  const toggleValue = optionValue => {
    const next = selectedSet.has(String(optionValue))
      ? value.filter(item => String(item) !== String(optionValue))
      : [...value, optionValue]
    onChange(next.length ? next : undefined)
  }
  return (
    <FilterDropdown
      label={label}
      activeCount={value.length || undefined}
      clearable={value.length > 0}
      onClear={() => onChange(undefined)}
      width={240}
    >
      {() => (
        <div className="fdai-gallery-search-menu">
          <SearchBox value={keyword} onChange={setKeyword} />
          <div className="fdai-gallery-menu-list">
            {filteredOptions.length ? filteredOptions.map(item => (
              <button
                key={item.value}
                type="button"
                className={`fdai-gallery-check-option ${selectedSet.has(String(item.value)) ? 'active' : ''}`}
                onClick={() => toggleValue(item.value)}
              >
                <span className="fdai-gallery-check-box" />
                <span title={item.label}>{item.label}</span>
              </button>
            )) : <div className="fdai-gallery-filter-empty">暂无数据</div>}
          </div>
        </div>
      )}
    </FilterDropdown>
  )
}

function InspirationSelect({ value = [], inspirationTree, onChange }) {
  const [keyword, setKeyword] = useState('')
  const [activeRootId, setActiveRootId] = useState()
  const selectedSet = useMemo(() => new Set(value.map(normalizeInspirationId)), [value])
  const roots = inspirationTree || []
  const currentRoot = roots.find(item => item.id === (activeRootId ?? roots[0]?.id)) || roots[0]
  const flatOptions = useMemo(() => flattenInspirationTree(roots), [roots])
  const filteredOptions = useMemo(() => {
    const text = keyword.trim().toLowerCase()
    if (!text) return []
    return flatOptions.filter(item => item.pathLabel.toLowerCase().includes(text))
  }, [flatOptions, keyword])
  const toggleId = id => {
    if (id === undefined || id === null) return
    const idKey = normalizeInspirationId(id)
    const next = selectedSet.has(idKey)
      ? value.filter(item => normalizeInspirationId(item) !== idKey)
      : [...value, id]
    onChange(next.length ? next : undefined)
  }
  const renderOption = item => (
    <button key={item.id} type="button" className={`fdai-gallery-check-option ${selectedSet.has(normalizeInspirationId(item.id)) ? 'active' : ''}`} onClick={() => toggleId(item.id)}>
      <span className="fdai-gallery-check-box" />
      <span title={item.pathLabel || item.name}>{item.pathLabel || item.name}</span>
    </button>
  )

  return (
    <FilterDropdown label="来源灵感集" labelClassName="inspiration" activeCount={value.length || undefined} clearable={value.length > 0} onClear={() => onChange(undefined)} width={500}>
      {() => (
        <div className="fdai-gallery-category-menu fdai-gallery-inspiration-menu">
          <SearchBox value={keyword} onChange={setKeyword} />
          {keyword.trim() ? (
            <div className="fdai-gallery-menu-list grid-two">
              {filteredOptions.length ? filteredOptions.map(renderOption) : <div className="fdai-gallery-filter-empty">暂无相关灵感集</div>}
            </div>
          ) : (
            <div className="fdai-gallery-cascade">
              <div className="fdai-gallery-cascade-left">
                {roots.map(root => {
                  const selected = selectedSet.has(normalizeInspirationId(root.id))
                  return (
                    <button
                      key={root.id}
                      type="button"
                      className={`fdai-gallery-inspiration-root ${root.id === currentRoot?.id ? 'active' : ''} ${selected ? 'selected' : ''}`}
                      onMouseEnter={() => setActiveRootId(root.id)}
                      onClick={() => toggleId(root.id)}
                    >
                      <span className="fdai-gallery-check-box" />
                      <span className="fdai-gallery-cascade-label" title={root.name}>{root.name}</span>
                    </button>
                  )
                })}
              </div>
              <div className="fdai-gallery-cascade-right">
                {currentRoot?.childs?.length ? currentRoot.childs.map(renderOption) : <div className="fdai-gallery-filter-empty">暂无子灵感集</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </FilterDropdown>
  )
}

function StyleSelect({ value, menuItems, onChange }) {
  const [keyword, setKeyword] = useState('')
  const options = useMemo(() => {
    const roots = getStyleOptions(menuItems)
    return roots.flatMap(root => [
      { ...root, pathLabel: root.name },
      ...(root.children || []).map(child => ({
        id: `${root.id}/${child.name}`,
        name: child.name,
        root: root.name,
        child: child.name,
        pathLabel: `${root.name} / ${child.name}`,
      })),
    ])
  }, [menuItems])
  const selectedIds = useMemo(() => new Set(value.map(item => item.id)), [value])
  const filteredOptions = useMemo(() => {
    const text = keyword.trim().toLowerCase()
    if (!text) return options
    return options.filter(item => item.pathLabel.toLowerCase().includes(text))
  }, [keyword, options])
  const toggleStyle = item => {
    if (selectedIds.has(item.id)) {
      onChange(value.filter(selected => selected.id !== item.id))
      return
    }
    onChange([...value, item])
  }

  return (
    <FilterDropdown label="穿搭风格" activeCount={value.length || undefined} clearable={value.length > 0} onClear={() => onChange([])} width={500}>
      {() => (
        <div className="fdai-gallery-search-menu">
          <SearchBox value={keyword} onChange={setKeyword} />
          <div className="fdai-gallery-menu-list grid-three">
            {filteredOptions.length ? filteredOptions.map(item => (
              <button key={item.id} type="button" className={`fdai-gallery-check-option ${selectedIds.has(item.id) ? 'active' : ''}`} onClick={() => toggleStyle(item)}>
                <span className="fdai-gallery-check-box" />
                <span title={item.pathLabel}>{keyword ? item.pathLabel : item.name}</span>
              </button>
            )) : <div className="fdai-gallery-filter-empty">暂无相关风格</div>}
          </div>
        </div>
      )}
    </FilterDropdown>
  )
}

export default function MeinianGalleryPanel({ open, refImgs, onSelect, onUnselect, onClose }) {
  const [filters, setFilters] = useState(emptyFilters)
  const [platformGroups, setPlatformGroups] = useState([])
  const [categoryTreeData, setCategoryTreeData] = useState(null)
  const [users, setUsers] = useState([])
  const [inspirationTree, setInspirationTree] = useState([])
  const [menuItems, setMenuItems] = useState([])
  const [filterLoaded, setFilterLoaded] = useState(false)
  const [galleryList, setGalleryList] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const scrollRef = useRef(null)
  const listRef = useRef([])
  const requestIdRef = useRef(0)
  const loadingRef = useRef(false)
  const convertingImageKeyRef = useRef(new Set())
  const failedConvertImageKeyRef = useRef(new Set())
  const queryParams = useMemo(() => buildMeinianParams(filters, menuItems), [filters, menuItems])
  const queryKey = useMemo(() => JSON.stringify(queryParams), [queryParams])
  const platformIdKey = filters.platformIdList?.join(',') || ''
  const userOptions = useMemo(() => users
    .filter(user => user.userId !== undefined)
    .map(user => ({ label: user.userName || String(user.userId), value: user.userId })), [users])

  useEffect(() => {
    listRef.current = galleryList
  }, [galleryList])

  useEffect(() => {
    if (!open || filterLoaded) return
    let cancelled = false
    const shopStatus = filters.platformIdList?.[0] === TAOXI_PLATFORM_VALUE ? 1 : 0
    Promise.all([
      apiFetchMeinianPlatforms().catch(() => []),
      apiFetchMeinianAllCategory({ shopStatus }).catch(() => null),
      apiFetchMeinianCollectUsers().catch(() => []),
      apiFetchMeinianInspirationTree().catch(() => []),
      apiFetchMeinianCommonMenuItems().catch(() => []),
    ]).then(([platforms, categoryTree, collectUsers, inspirations, commonMenuItems]) => {
      if (cancelled) return
      setPlatformGroups(toList(platforms))
      setCategoryTreeData(toObject(categoryTree))
      setUsers(toList(collectUsers))
      setInspirationTree(toList(inspirations))
      setMenuItems(toList(commonMenuItems))
      setFilterLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [filterLoaded, open, platformIdKey])

  useEffect(() => {
    if (!open) return
    const shopStatus = filters.platformIdList?.[0] === TAOXI_PLATFORM_VALUE ? 1 : 0
    apiFetchMeinianAllCategory({ shopStatus })
      .then(res => setCategoryTreeData(toObject(res)))
      .catch(() => setCategoryTreeData(null))
  }, [open, platformIdKey])

  const loadGallery = useCallback(async (reset = false) => {
    if (!open || (!reset && loadingRef.current)) return
    loadingRef.current = true
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    const currentList = reset ? [] : listRef.current
    const start = currentList.length
    const lastItem = currentList[currentList.length - 1]
    if (reset) setLoading(true)
    else setLoadingMore(true)
    try {
      const res = await apiFetchMeinianGallery({
        ...queryParams,
        start,
        pageSize: PAGE_SIZE,
        sortValues: reset ? undefined : lastItem?.sortValues,
      }, { start, pageSize: PAGE_SIZE })
      if (requestIdRef.current !== requestId) return
      const list = toList(res).filter(item => item?.mainUrl)
      const total = Number(res?.resultCount ?? res?.total ?? res?.data?.resultCount ?? res?.data?.total)
      const nextList = dedupeGalleryList(reset ? list : [...currentList, ...list])
      setGalleryList(nextList)
      setHasMore(Number.isNaN(total) ? list.length === PAGE_SIZE : nextList.length < total)
    } catch (error) {
      if (requestIdRef.current === requestId) {
        if (reset) setGalleryList([])
        setHasMore(false)
      }
    } finally {
      if (requestIdRef.current === requestId) {
        loadingRef.current = false
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [open, queryParams])

  useEffect(() => {
    if (!open) {
      setGalleryList([])
      setHasMore(true)
      return
    }
    setGalleryList([])
    listRef.current = []
    convertingImageKeyRef.current.clear()
    failedConvertImageKeyRef.current.clear()
    setHasMore(true)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    loadGallery(true)
  }, [loadGallery, open, queryKey])

  const handleScroll = event => {
    const el = event.currentTarget
    if (hasMore && !loading && !loadingMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      loadGallery(false)
    }
  }

  const handleSelect = item => {
    const id = getImageId(item)
    const selectedRefImg = findSelectedRefImage(item, refImgs)
    if (selectedRefImg) {
      onUnselect?.(selectedRefImg.url, selectedRefImg.id ?? id)
      return
    }
    if (refImgs.length >= MAX_REF_IMGS) return
    onSelect(getGalleryImageUrl(item), id)
  }

  const handleImageError = useCallback(item => {
    const key = getImageKey(item)
    const originUrl = item.originMainUrl || item.mainUrl
    if (key == null || key === '' || !originUrl || item.convertedMainUrl || convertingImageKeyRef.current.has(key) || failedConvertImageKeyRef.current.has(key)) return

    convertingImageKeyRef.current.add(key)
    apiUploadTmpImg(originUrl)
      .then(res => {
        const convertedUrl = getUploadedImageUrl(res)
        if (!convertedUrl || convertedUrl === originUrl) {
          failedConvertImageKeyRef.current.add(key)
          return
        }
        setGalleryList(prev => prev.map(galleryItem => {
          if (getImageKey(galleryItem) !== key) return galleryItem
          return {
            ...galleryItem,
            originMainUrl: galleryItem.originMainUrl || galleryItem.mainUrl,
            convertedMainUrl: convertedUrl,
          }
        }))
      })
      .catch(() => {
        failedConvertImageKeyRef.current.add(key)
      })
      .finally(() => {
        convertingImageKeyRef.current.delete(key)
      })
  }, [])

  return (
    <div className={`fdai-meinian-gallery-panel ${open ? 'open' : ''}`}>
      <div className="fdai-meinian-gallery-panel-inner">
        <div className="fdai-gallery-header">
          <div className="fdai-gallery-title">美念图库</div>
          <button type="button" className="fdai-gallery-close" aria-label="关闭美念图库" onClick={onClose}>
            <span className="fdai-gallery-close-icon" />
          </button>
        </div>
        <div className="fdai-gallery-filters">
          <PlatformSelect
            value={filters.platformIdList}
            platformGroups={platformGroups}
            onChange={platformIdList => setFilters(prev => ({
              ...prev,
              platformIdList,
            }))}
          />
          <CategorySelect
            gender={filters.gender}
            category={filters.category}
            categoryTreeData={categoryTreeData}
            onChange={(gender, category) => setFilters(prev => ({ ...prev, gender, category }))}
          />
          <MultiSearchSelect
            label="采集成员"
            value={filters.userIdList || []}
            options={userOptions}
            onChange={userIdList => setFilters(prev => ({
              ...prev,
              userIdList: userIdList?.map(userId => Number(userId)),
            }))}
          />
          <InspirationSelect
            value={filters.inspirationIdList || []}
            inspirationTree={inspirationTree}
            onChange={inspirationIdList => setFilters(prev => ({ ...prev, inspirationIdList }))}
          />
          <StyleSelect
            value={filters.selectedStyles}
            menuItems={menuItems}
            onChange={selectedStyles => setFilters(prev => ({ ...prev, selectedStyles }))}
          />
          {(
            filters.platformIdList?.length
            || filters.gender
            || filters.category
            || filters.userIdList?.length
            || filters.inspirationIdList?.length
            || filters.selectedStyles.length
          ) ? (
            <button
              type="button"
              className="fdai-gallery-clear-all"
              onClick={() => setFilters(emptyFilters())}
            >
              清空
            </button>
          ) : null}
        </div>
        <div className="fdai-gallery-list" ref={scrollRef} onScroll={handleScroll}>
          {loading ? <div className="fdai-gallery-state">加载中...</div> : null}
          {!loading && galleryList.length === 0 ? <div className="fdai-gallery-state">暂无相关款式</div> : null}
          {!loading && galleryList.length ? (
            <div className="fdai-gallery-grid">
              {galleryList.map(item => {
                const id = getImageKey(item)
                const selected = isSelectedImage(item, refImgs)
                return (
                  <button key={id} type="button" className={`fdai-gallery-card ${selected ? 'selected' : ''}`} onClick={() => handleSelect(item)}>
                    <img src={getGalleryImageUrl(item)} alt="" referrerPolicy="no-referrer" onError={() => handleImageError(item)} />
                    {selected ? <span className="fdai-gallery-selected-icon" /> : null}
                  </button>
                )
              })}
            </div>
          ) : null}
          {loadingMore ? <div className="fdai-gallery-loading-more">加载更多...</div> : null}
        </div>
      </div>
    </div>
  )
}
